import useLevelDisplayInfo from '@api/hooks/profile/useLevelDisplayInfo';
import { EDUCATOR_TYPES } from '@cont/Batch/utils';
import styled from '@emotion/styled';
import { Fonts, Spacings, mediaQuery } from '@styles/index';
import StarCircleOutline from '@icon/StarCircleOutline';
import Language from '@icon/Language';
import Dedications from '@icon/Dedications';
import Credit from '@icon/Credit';
import { Typography } from 'aquilla/core/atoms/Typography';
import dateHelper from '@utils/dates';
import { useEffect, useState } from 'react';
import ChevronRight from '@icon/ChevronRight';
import { emptyObject } from '@constants/empty';
import EducatorLevelTagNew from '../EducatorLevelTagNew';
import EducatorDedicationStats from '../EducatorDedicationStats';
import EducatorHighlightsDrawer from '../EducatorHighlightsDrawer';

const Section = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${Spacings.SPACING_6B};

  ${mediaQuery.uptoMobile} {
    gap: ${Spacings.SPACING_3B};
    padding: ${Spacings.SPACING_3B} ${Spacings.SPACING_4B};
    background-color: var(--color-base-1);
  }
`;

const HighlightsWrapper = styled.div`
  grid-area: highlights;
  display: flex;
  flex-direction: column;
  gap: ${Spacings.SPACING_8B};

  ${mediaQuery.uptoMobile} {
    gap: ${Spacings.SPACING_2B};
  }
`;
const RankingSection = styled(Section)`
  [class*='EducatorLevelWrapper'] {
    > svg {
      height: 24px;
      width: 24px;
    }
    > [class*='EducatorLevel'] {
      font-size: 20px;
      font-weight: ${Fonts.HEAVY};
    }
  }

  ${mediaQuery.uptoMobile} {
    display: none;
  }
`;
const HightlightSection = styled(Section)``;
const DedicationSection = styled(Section)`
  [class*='MuiPaper-root'] {
    display: contents;
    > div:first-child {
      display: none;
    }
    hr {
      display: none;
    }
    > div:last-child {
      justify-content: flex-end;

      ${mediaQuery.uptoMobile} {
        margin-left: ${Spacings.SPACING_4B};
      }
    }
  }
`;
const Stats = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${Spacings.SPACING_4B};
`;
const Highlight = styled.div`
  display: flex;
  gap: ${Spacings.SPACING_3B};
  > svg {
    flex-shrink: 0;
  }
  > h6 {
    > p {
      display: inline;
    }
  }
`;

const ViewMore = styled.div`
  display: flex;
  margin-top: ${Spacings.SPACING_2B};
  align-items: center;
  cursor: pointer;
`;

const Icons = {
  StarCircleOutline,
  Language,
  Dedications,
  Credit
};

const SectionHeader = ({ children }) => (
  <Typography variant="h4" color="var(--color-text-primary)">
    {children}
  </Typography>
);

const EducatorCredential = ({ icon, highlightedText, baseText, atStart }) => {
  const IconComponent = Icons[icon];
  return (
    <Highlight>
      <IconComponent size="18px" color="var(--color-progress-grey)" />
      <Typography variant="h6" color="var(--color-text-primary)">
        {atStart && highlightedText && `${highlightedText} `}
        {baseText && (
          <Typography variant="p2" color="var(--color-text-secondary)">
            {baseText}
          </Typography>
        )}
        {!atStart && highlightedText && ` ${highlightedText}`}
      </Typography>
    </Highlight>
  );
};

const EducatorHighlights = ({ educator, username }) => {
  const { levelsDisplayInfo = [] } = useLevelDisplayInfo();
  const {
    educatorTypeV2,
    credentials,
    languages,
    isExEducator,
    becomeEducatorAt,
    firstName,
    knowsAbout,
    dateJoined,
    dedications
  } = educator;
  const showViewMore = credentials?.find(
    (credential) => credential.value?.length > 1
  );
  const [showDetailedHighlights, setShowDetailedHighlights] = useState(false);
  const onClickViewMore = () => {
    setShowDetailedHighlights(true);
  };

  const onCloseHighlights = () => setShowDetailedHighlights(false);

  useEffect(() => {
    setShowDetailedHighlights(false);
  }, [username]);
  const isPartnerEducator = EDUCATOR_TYPES.PARTNER === educatorTypeV2;
  const educatorLevel =
    levelsDisplayInfo.find(
      (level) => level.levelId === educator.levelInfo?.currentLevelId
    ) || {};
  const workedAt = credentials?.find((item) => item.type === 1)?.value?.[0];
  const studiedAt = credentials?.find((item) => item.type === 2)?.value?.[0];
  const accomplishment = credentials?.find((item) => item.type === 4)
    ?.value?.[0];

  const becomeEducatorAtString = becomeEducatorAt
    ? `${dateHelper.getDateMonthString(
        becomeEducatorAt,
        true
      )}, ${dateHelper.getYear(becomeEducatorAt)}`
    : null;
  const languagesCount = languages?.length;
  const languageString =
    languages &&
    (languagesCount > 1
      ? `${languages?.slice(0, languagesCount - 1).join(', ')} and ${
          languages[languagesCount - 1]
        }`
      : languages?.[0]);
  const levelInfo = isExEducator ? emptyObject : educator?.levelInfo;
  if (!Array.isArray(credentials)) return null;
  return (
    <HighlightsWrapper>
      {educatorLevel?.name && (
        <RankingSection>
          <SectionHeader>Educator Level</SectionHeader>
          <EducatorLevelTagNew
            educatorLevel={educatorLevel}
            isPartnerEducator={isPartnerEducator}
            isExEducator={isExEducator}
            levelInfo={levelInfo}
          />
        </RankingSection>
      )}
      <HightlightSection>
        <SectionHeader>Educator highlights</SectionHeader>
        <Stats>
          {workedAt && (
            <EducatorCredential
              icon="StarCircleOutline"
              baseText={workedAt.currently_related ? 'Works at' : 'Worked at'}
              highlightedText={workedAt.title}
            />
          )}
          {studiedAt && (
            <EducatorCredential
              icon="Dedications"
              baseText={
                studiedAt.currently_related ? 'Studies at' : 'Studied at'
              }
              highlightedText={studiedAt.title}
            />
          )}
          {accomplishment && (
            <EducatorCredential
              icon="StarCircleOutline"
              baseText={accomplishment.title}
            />
          )}
          {languageString && (
            <EducatorCredential
              icon="Language"
              baseText="Knows"
              highlightedText={languageString}
            />
          )}
          {becomeEducatorAtString && (
            <EducatorCredential
              icon="Credit"
              baseText="Unacademy Educator since"
              highlightedText={becomeEducatorAtString}
            />
          )}
        </Stats>
        {showViewMore && (
          <ViewMore onClick={onClickViewMore}>
            <Typography variant="p2" color="var(--light-text-primary)">
              View more
            </Typography>
            <ChevronRight size="16px" />
          </ViewMore>
        )}
      </HightlightSection>
      {dedications && dedications?.length !== 0 && (
        <DedicationSection>
          <SectionHeader>Dedications</SectionHeader>
          <EducatorDedicationStats username={username} trimInMobile={false} />
        </DedicationSection>
      )}
      {showDetailedHighlights && (
        <EducatorHighlightsDrawer
          showDetailedHighlights={showDetailedHighlights}
          onCloseHighlights={onCloseHighlights}
          firstName={firstName}
          credentials={credentials}
          languages={languages}
          dateOfJoining={dateJoined}
          knowsAbout={knowsAbout}
        />
      )}
    </HighlightsWrapper>
  );
};

export default EducatorHighlights;
